import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../api";
import { ArrowLeft, Printer, Loader2, Check, X } from "lucide-react";
import {
  BAG_FRONT_POCKET,
  BAG_CENTRAL_POCKET,
  BAG_BACK_POCKET,
} from "../lib/bagChecklistFields";

function Row({
  label,
  qty,
  on,
}: {
  label: string;
  qty: string;
  on: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm">
      <span className="text-slate-700 flex-1">
        {label} <span className="text-xs text-slate-400">(×{qty})</span>
      </span>
      <span
        className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${
          on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
        }`}
      >
        {on ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
        {on ? "Yes" : "No"}
      </span>
    </div>
  );
}

function PocketCard({
  title,
  items,
  values,
}: {
  title: string;
  items: ReadonlyArray<readonly [string, string, string]>;
  values: Record<string, boolean> | undefined;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden break-inside-avoid">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-semibold text-slate-800">
        {title}
      </div>
      <div className="divide-y divide-slate-100">
        {items.map(([k, label, qty]) => (
          <Row key={k} label={label} qty={qty} on={!!values?.[k]} />
        ))}
      </div>
    </div>
  );
}

export function BagChecklistDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const api = useApi();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const q = useQuery({ queryKey: ["bag-checklist", id, api.token], queryFn: () => api.get<any>(`/api/v1/bag-checklists/${id}`), enabled: Boolean(api.token) && Boolean(id) });
  const data = q.isPending ? undefined : q.isError ? null : q.data;

  if (data === undefined) {
    return (
      <div className="flex items-center justify-center py-20 text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading bag checklist…
      </div>
    );
  }
  if (data === null) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-700 mb-4">Bag checklist not found.</p>
        <button
          onClick={() => navigate({ to: "/bag-checklists" })}
          className="px-4 py-2 rounded-lg bg-slate-200 text-slate-800 hover:bg-slate-300"
        >
          Back to bag checklists
        </button>
      </div>
    );
  }

  const info: [string, string][] = [
    ["Hospital", data.hospitalName],
    ["Date", data.date],
    ["Shift", data.shift === "D" ? "Day" : "Night"],
    [
      "Checked by",
      `${data.checkedByName || "—"}${
        data.checkedByBadge ? ` (${data.checkedByBadge})` : ""
      }`,
    ],
    ["Submitted", new Date(data.submittedAt).toLocaleString(undefined, { hour12: false })],
  ];

  return (
    <div className="force-print-colors bag-print max-w-4xl mx-auto">
      <div className="no-print flex items-center justify-between mb-4 gap-2">
        <button
          onClick={() => navigate({ to: "/bag-checklists" })}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-[var(--hmg-red)] text-white hover:bg-[var(--hmg-red-dark)] text-sm font-semibold"
        >
          <Printer className="w-4 h-4" />
          <span className="hidden sm:inline">Print / Save PDF</span>
          <span className="sm:hidden">Print</span>
        </button>
      </div>

      <div className="flex items-center gap-3 sm:gap-4 border-b-2 border-[var(--hmg-red)] pb-3 mb-5">
        <img
          src="/hmg-logo.png"
          alt="Dr Sulaiman Al Habib Medical Group"
          width={240}
          height={62}
          className="h-11 sm:h-14 w-auto flex-shrink-0"
        />
        <div className="border-l border-slate-300 pl-3 sm:pl-4">
          <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--hmg-red)] font-bold">
            Emergency Medical Services
          </div>
          <div className="text-base sm:text-xl font-bold text-slate-900">
            ACLS Bag Checklist
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 break-inside-avoid">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {info.map(([k, v]) => (
            <div key={k}>
              <div className="text-xs uppercase tracking-wide text-slate-400">
                {k}
              </div>
              <div className="text-sm font-medium text-slate-800">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {(() => {
        const rec = data as Record<string, unknown>;
        const pocket = (k: string) =>
          rec[k] as Record<string, boolean> | undefined;
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {/* Two small pockets stacked together; the long Central pocket fills its own column. */}
            <div className="space-y-4">
              <PocketCard
                title="Front Pocket"
                items={BAG_FRONT_POCKET}
                values={pocket("frontPocket")}
              />
              <PocketCard
                title="Back Pocket"
                items={BAG_BACK_POCKET}
                values={pocket("backPocket")}
              />
            </div>
            <PocketCard
              title="Central Pocket"
              items={BAG_CENTRAL_POCKET}
              values={pocket("centralPocket")}
            />
          </div>
        );
      })()}
    </div>
  );
}
